import apiClient from '@/lib/api-client'
import type { CoachBackground, CoachTrack } from '@/types/coach'
import type {
  CoachAssignment,
  CoachBooking,
  CoachChatMessage,
  CoachChatThread,
  CoachDirectoryItem,
  CoachExternalReview,
  CoachExternalReviewPage,
  CoachGroupSession,
  CoachGroupSessionWrite,
  CoachMatchPreferences,
  CoachMatchRecommendation,
  CoachMatchRequest,
  CoachNotificationPreferences,
  CoachPlan,
  CoachReview,
  CoachReviewPage,
  CoachReviewWrite,
  CoachSlot,
} from '@/types/coach-booking'

export const coachBookingService = {
  async notificationPreferences(): Promise<CoachNotificationPreferences> {
    return (
      await apiClient.get<CoachNotificationPreferences>('/api/v1/coach/notification-preferences')
    ).data
  },
  async updateNotificationPreferences(
    payload: CoachNotificationPreferences,
  ): Promise<CoachNotificationPreferences> {
    return (
      await apiClient.put<CoachNotificationPreferences>(
        '/api/v1/coach/notification-preferences',
        payload,
      )
    ).data
  },
  async directory(
    track: CoachTrack = 'general',
    focus: string[] = [],
    industry: string | null = null,
  ): Promise<CoachDirectoryItem[]> {
    return (
      await apiClient.get<CoachDirectoryItem[]>('/api/v1/coach/directory', {
        params: {
          track,
          ...(focus.length ? { focus: focus.join(',') } : {}),
          ...(industry ? { industry } : {}),
        },
      })
    ).data
  },
  async coach(profileId: number): Promise<CoachDirectoryItem> {
    return (await apiClient.get<CoachDirectoryItem>(`/api/v1/coach/directory/${profileId}`)).data
  },
  async setSaved(profileId: number, saved: boolean): Promise<void> {
    await apiClient.put(`/api/v1/coach/directory/${profileId}/saved`, null, {
      params: { saved },
    })
  },
  async availabilityAlerts(): Promise<number[]> {
    return (await apiClient.get<number[]>('/api/v1/coach/availability-alerts')).data
  },
  async setAvailabilityAlert(profileId: number, active: boolean): Promise<void> {
    await apiClient.put(`/api/v1/coach/directory/${profileId}/availability-alert`, null, {
      params: { active },
    })
  },
  async groupSessions(): Promise<CoachGroupSession[]> {
    return (await apiClient.get<CoachGroupSession[]>('/api/v1/coach/group-sessions')).data
  },
  async myGroupSessions(): Promise<CoachGroupSession[]> {
    return (await apiClient.get<CoachGroupSession[]>('/api/v1/coach/group-sessions/mine')).data
  },
  async createGroupSession(payload: CoachGroupSessionWrite): Promise<CoachGroupSession> {
    return (await apiClient.post<CoachGroupSession>('/api/v1/coach/group-sessions', payload)).data
  },
  async updateGroupSession(
    id: number,
    payload: CoachGroupSessionWrite,
  ): Promise<CoachGroupSession> {
    return (await apiClient.put<CoachGroupSession>(`/api/v1/coach/group-sessions/${id}`, payload))
      .data
  },
  async setGroupRegistration(id: number, registered: boolean): Promise<void> {
    await apiClient.put(`/api/v1/coach/group-sessions/${id}/registration`, null, {
      params: { registered },
    })
  },
  async slots(profileId: number, sessionTypeId: number, startDate: string): Promise<CoachSlot[]> {
    return (
      await apiClient.get<CoachSlot[]>(`/api/v1/coach/directory/${profileId}/slots`, {
        params: {
          session_type_id: sessionTypeId,
          start_date: startDate,
          days: 14,
        },
      })
    ).data
  },
  async book(payload: {
    coach_profile_id: number
    session_type_id: number
    scheduled_at: string
    booking_answers: Array<{ question: string; answer: string }>
    candidate_note: string | null
    candidate_timezone: string
    service_track: CoachTrack
  }): Promise<CoachBooking> {
    return (await apiClient.post<CoachBooking>('/api/v1/coach/bookings', payload)).data
  },
  async myBookings(): Promise<CoachBooking[]> {
    return (await apiClient.get<CoachBooking[]>('/api/v1/coach/bookings/me')).data
  },
  async myReviews(): Promise<CoachReview[]> {
    return (await apiClient.get<CoachReview[]>('/api/v1/coach/reviews/me')).data
  },
  async publicReviews(profileId: number): Promise<CoachReview[]> {
    return (await apiClient.get<CoachReview[]>(`/api/v1/coach/directory/${profileId}/reviews`)).data
  },
  async publicReviewPage(
    profileId: number,
    limit: number,
    offset: number,
  ): Promise<CoachReviewPage> {
    return (
      await apiClient.get<CoachReviewPage>(`/api/v1/coach/directory/${profileId}/reviews/page`, {
        params: { limit, offset },
      })
    ).data
  },
  async externalReviews(profileId: number): Promise<CoachExternalReview[]> {
    return (
      await apiClient.get<CoachExternalReview[]>(
        `/api/v1/coach/directory/${profileId}/external-reviews`,
      )
    ).data
  },
  async externalReviewPage(
    profileId: number,
    limit: number,
    offset: number,
  ): Promise<CoachExternalReviewPage> {
    return (
      await apiClient.get<CoachExternalReviewPage>(
        `/api/v1/coach/directory/${profileId}/external-reviews/page`,
        { params: { limit, offset } },
      )
    ).data
  },
  async recommendations(payload: CoachMatchPreferences): Promise<CoachMatchRecommendation[]> {
    return (
      await apiClient.post<CoachMatchRecommendation[]>(
        '/api/v1/coach/matching/recommendations',
        payload,
      )
    ).data
  },
  async assignment(): Promise<CoachAssignment | null> {
    return (await apiClient.get<CoachAssignment | null>('/api/v1/coach/assignment')).data
  },
  async matchedCoachBackground(profileId: number): Promise<CoachBackground> {
    return (await apiClient.get<CoachBackground>(`/api/v1/coach/directory/${profileId}/background`))
      .data
  },
  async matchRequest(): Promise<CoachMatchRequest | null> {
    return (await apiClient.get<CoachMatchRequest | null>('/api/v1/coach/matching/request')).data
  },
  async submitMatchRequest(payload: CoachMatchPreferences): Promise<CoachMatchRequest> {
    return (await apiClient.post<CoachMatchRequest>('/api/v1/coach/matching/requests', payload))
      .data
  },
  async createReview(bookingId: number, payload: CoachReviewWrite): Promise<CoachReview> {
    return (
      await apiClient.post<CoachReview>(`/api/v1/coach/bookings/${bookingId}/review`, payload)
    ).data
  },
  async updateReview(bookingId: number, payload: CoachReviewWrite): Promise<CoachReview> {
    return (await apiClient.put<CoachReview>(`/api/v1/coach/bookings/${bookingId}/review`, payload))
      .data
  },
  async deleteReview(bookingId: number): Promise<void> {
    await apiClient.delete(`/api/v1/coach/bookings/${bookingId}/review`)
  },
  async coachReviews(): Promise<CoachReview[]> {
    return (await apiClient.get<CoachReview[]>('/api/v1/coach/reviews/coach')).data
  },
  async coachReviewPage(limit: number, offset: number): Promise<CoachReviewPage> {
    return (
      await apiClient.get<CoachReviewPage>('/api/v1/coach/reviews/coach/page', {
        params: { limit, offset },
      })
    ).data
  },
  async reportReview(reviewId: number): Promise<CoachReview> {
    return (await apiClient.post<CoachReview>(`/api/v1/coach/reviews/${reviewId}/report`)).data
  },
  async cancel(id: number, reason: string | null): Promise<CoachBooking> {
    return (await apiClient.post<CoachBooking>(`/api/v1/coach/bookings/${id}/cancel`, { reason }))
      .data
  },
  async reschedule(
    id: number,
    scheduledAt: string,
    candidateTimezone: string,
  ): Promise<CoachBooking> {
    return (
      await apiClient.put<CoachBooking>(`/api/v1/coach/bookings/${id}/reschedule`, {
        scheduled_at: scheduledAt,
        candidate_timezone: candidateTimezone,
      })
    ).data
  },
  async retryMeeting(id: number): Promise<CoachBooking> {
    return (await apiClient.post<CoachBooking>(`/api/v1/coach/bookings/${id}/meeting/retry`)).data
  },
  async conversations(): Promise<CoachChatThread[]> {
    return (await apiClient.get<CoachChatThread[]>('/api/v1/coach/conversations')).data
  },
  async socketTicket(threadId: number): Promise<string> {
    return (
      await apiClient.post<{ ticket: string }>(
        `/api/v1/coach/conversations/${threadId}/socket-ticket`,
      )
    ).data.ticket
  },
  async openConversation(bookingId: number): Promise<CoachChatThread> {
    return (
      await apiClient.post<CoachChatThread>(`/api/v1/coach/bookings/${bookingId}/conversation`)
    ).data
  },
  async sendMessage(
    threadId: number,
    payload: { client_message_id: string; body: string },
  ): Promise<CoachChatMessage> {
    return (
      await apiClient.post<CoachChatMessage>(
        `/api/v1/coach/conversations/${threadId}/messages`,
        payload,
      )
    ).data
  },
  async markConversationRead(threadId: number): Promise<CoachChatThread> {
    return (await apiClient.put<CoachChatThread>(`/api/v1/coach/conversations/${threadId}/read`))
      .data
  },
  async markAssignmentSeen(threadId: number): Promise<CoachChatThread> {
    return (
      await apiClient.put<CoachChatThread>(
        `/api/v1/coach/conversations/${threadId}/assignment-seen`,
      )
    ).data
  },
  async updateCandidateAction(
    bookingId: number,
    actionIndex: number,
    completed: boolean,
  ): Promise<CoachBooking> {
    return (
      await apiClient.put<CoachBooking>(
        `/api/v1/coach/bookings/${bookingId}/actions/${actionIndex}`,
        { completed },
      )
    ).data
  },
  async updatePlan(
    candidateUserId: number,
    payload: Omit<
      CoachPlan,
      | 'id'
      | 'coach_profile_id'
      | 'candidate_user_id'
      | 'originating_session_id'
      | 'created_at'
      | 'updated_at'
    >,
  ): Promise<CoachPlan> {
    return (await apiClient.put<CoachPlan>(`/api/v1/coach/plans/${candidateUserId}`, payload)).data
  },
  async plans(): Promise<CoachPlan[]> {
    return (await apiClient.get<CoachPlan[]>('/api/v1/coach/plans')).data
  },
  async coachSessions(): Promise<CoachBooking[]> {
    return (await apiClient.get<CoachBooking[]>('/api/v1/coach/sessions')).data
  },
  async cancelCoachSession(id: number, reason: string | null): Promise<CoachBooking> {
    return (await apiClient.post<CoachBooking>(`/api/v1/coach/sessions/${id}/cancel`, { reason }))
      .data
  },
  async rescheduleCoachSession(id: number, scheduledAt: string): Promise<CoachBooking> {
    return (
      await apiClient.put<CoachBooking>(`/api/v1/coach/sessions/${id}/reschedule`, {
        scheduled_at: scheduledAt,
      })
    ).data
  },
  async updateWorkspace(
    id: number,
    payload: {
      status: CoachBooking['status']
      coach_notes: string | null
      action_items: Array<{ title: string; completed: boolean }>
      shared_resources: Array<{ title: string; url: string }>
    },
  ): Promise<CoachBooking> {
    return (await apiClient.put<CoachBooking>(`/api/v1/coach/sessions/${id}`, payload)).data
  },
}
